import PropTypes from 'prop-types';
import './style.scss';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import React, { useEffect, useMemo } from 'react';
import IfRender from '../../IfRender/index.jsx';

const XFormSelect = React.forwardRef(
  (
    {
      id = '',
      name = '',
      label = '',
      readOnly = false,
      placeholder = '',
      labelClass = null,
      labelName = null,
      wrapClass = null,
      selectClass = null,
      getDataFromChild,
      optionClass = null,
      disabled = false,
      errorLabelClass = null,
      errorMessage = null,
      disabledValues = [],
      blankOptionValue = '',
      hiddenBlankOption = true,
      disabledBlankOption = true,
      options = [],
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const {
      formState: { errors },
      control,
      setValue
    } = useFormContext();

    const renderOptions = useMemo(() => {
      const placeholderOption = !hiddenBlankOption ? (
        <option
          key={`${id}-placeholder`}
          value={blankOptionValue}
          disabled={disabledBlankOption}
          hidden={hiddenBlankOption}>
          {placeholder}
        </option>
      ) : null;

      const mappedOptions = options.map((option, index) => (
        <option
          key={`${id}-option${index}`}
          className={`select__option ${optionClass}`}
          value={option.value}
          disabled={disabledValues.includes(option.value)}>
          {option.label}
        </option>
      ));

      return [placeholderOption, ...mappedOptions];
    }, [
      options,
      placeholder,
      optionClass,
      id,
      blankOptionValue,
      hiddenBlankOption,
      disabledBlankOption,
      disabledValues
    ]);

    return (
      <fieldset className={`select__wrap ${wrapClass}`}>
        <IfRender condition={label}>
          <label htmlFor={id} className={`select__label`}>
            {label}
          </label>
        </IfRender>
        <Controller
          ref={ref}
          {...props}
          name={name}
          control={control}
          placeholder={placeholder}
          render={(props) => {
            const { onChange, name, value } = props.field;
            return (
              <select
                id={id}
                name={name}
                disabled={disabled}
                className={`select__dropdown ${selectClass}`}
                value={value ?? ''}
                onChange={(option) => {
                  onChange(option.currentTarget.value);
                  setValue(name, option.currentTarget.value);
                  if (getDataFromChild) {
                    getDataFromChild(option.currentTarget.value);
                  }
                }}>
                {renderOptions}
              </select>
            );
          }}
        />
        <IfRender condition={errors?.[name]}>
          <label htmlFor={id} className={`input__label input__label--error ${errorLabelClass}`}>
            {t(errors?.[name]?.message)}
          </label>
        </IfRender>
      </fieldset>
    );
  }
);

XFormSelect.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  defaultValue: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  labelClass: PropTypes.string,
  wrapClass: PropTypes.string,
  selectClass: PropTypes.string,
  optionClass: PropTypes.string,
  errorLabelClass: PropTypes.string,
  errorMessage: PropTypes.string,
  getDataFromChild: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  disabledValues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  blankOptionValue: PropTypes.string,
  hiddenBlankOption: PropTypes.string,
  disabledBlankOption: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  )
};

export default XFormSelect;
